/**
 * Offline notes in the Android app (spec11): the outbox of local changes, conflict copies,
 * and what the server does for notes online (search, links, backlinks) on the local copy.
 */

import { zonedParts } from "./dates.js";
import { extractWikiLinks, linkKey } from "./notes.js";

/** The note fields a local change can set. */
export interface NoteFields {
  /** Only on create. */
  kind?: "markdown" | "canvas";
  title?: string;
  content?: string;
  pinned?: boolean;
  notebookId?: string | null;
  /** In the trash (true) or restored from it (false). */
  deleted?: boolean;
}

/** The notebook fields a local change can set. */
export interface NotebookFields {
  name?: string;
  parentId?: string | null;
  deleted?: boolean;
}

/** One queued change per note or notebook (FR6), merged as the user keeps editing. */
export interface OutboxEntry<F = NoteFields> {
  id: string;
  /** "destroy": deleted for good (FR6a). */
  op: "create" | "update" | "destroy";
  /** Fields to send; for a create, everything the item starts with. */
  fields: F;
  /** Server version the content edit started from (content saves only). */
  baseVersion: number | null;
  /** "failed": the server refused it for good (FR11) until the user retries. */
  status: "pending" | "failed";
  error: string | null;
  /** When the first change was queued; entries are sent in this order. */
  queuedAt: string;
  /** Bumped on every merge, so a sync can tell whether the entry changed while it was sent. */
  rev: number;
}

export type Change<F> =
  | { op: "create"; fields: F }
  | { op: "update"; fields: F; baseVersion?: number }
  | { op: "destroy" };

/**
 * Merges a change into the item's queued entry. Null: nothing is left to send (an item
 * created and deleted for good before it ever reached the server).
 */
export function queueChange<F extends object = NoteFields>(
  entry: OutboxEntry<F> | undefined,
  id: string,
  change: Change<F>,
  now: Date,
): OutboxEntry<F> | null {
  const fresh = (op: OutboxEntry<F>["op"], fields: F, baseVersion: number | null) => ({
    id,
    op,
    fields,
    baseVersion,
    status: "pending" as const,
    error: null,
    queuedAt: entry?.queuedAt ?? now.toISOString(),
    rev: (entry?.rev ?? 0) + 1,
  });
  if (change.op === "create") return fresh("create", change.fields, null);
  if (change.op === "destroy") {
    return entry?.op === "create" ? null : fresh("destroy", {} as F, null);
  }
  if (entry?.op === "destroy") return entry;
  const fields = { ...entry?.fields, ...change.fields } as F;
  if (entry?.op === "create") return fresh("create", fields, null);
  const base =
    entry?.baseVersion ??
    ("content" in change.fields && change.fields.content !== undefined
      ? (change.baseVersion ?? null)
      : null);
  return fresh("update", fields, base);
}

/** Outbox entries in the order they are sent. */
export const outboxOrder = <F>(a: OutboxEntry<F>, b: OutboxEntry<F>) =>
  a.queuedAt === b.queuedAt ? a.id.localeCompare(b.id) : a.queuedAt < b.queuedAt ? -1 : 1;

/** "Plan (conflict 2026-10-25 14:03)", in the display timezone (FR8). */
export function conflictTitle(title: string, at: Date, timeZone: string): string {
  const { date, time } = zonedParts(at, timeZone);
  const suffix = ` (conflict ${date} ${time})`;
  const base = title.trim() || "Untitled";
  return `${base.slice(0, 200 - suffix.length)}${suffix}`;
}

/** What local search, links and backlinks need of a note. */
export interface LocalNoteText {
  id: string;
  kind: "markdown" | "canvas";
  title: string;
  notebookId: string | null;
  updatedAt: string;
  /** Null for canvases (their content is not kept offline). */
  content: string | null;
}

export interface SnippetPart {
  text: string;
  match: boolean;
}

export interface LocalSearchResult {
  id: string;
  title: string;
  notebookId: string | null;
  snippet: SnippetPart[];
}

const MAX_SEARCH_WORDS = 10;
const SNIPPET_LENGTH = 160;

/** Search words like the server's (letters and digits only), lower case. */
export function searchWords(text: string): string[] {
  return (
    text
      .normalize("NFC")
      .toLowerCase()
      .match(/[\p{L}\p{N}]+/gu)
      ?.slice(0, MAX_SEARCH_WORDS) ?? []
  );
}

/** Plain and matched parts of `line`, every occurrence of any word marked. */
export function highlight(line: string, words: readonly string[]): SnippetPart[] {
  const lower = line.toLowerCase();
  const marks: Array<[number, number]> = [];
  for (const word of words) {
    for (let at = lower.indexOf(word); at !== -1; at = lower.indexOf(word, at + word.length)) {
      marks.push([at, at + word.length]);
    }
  }
  marks.sort((a, b) => a[0] - b[0]);
  const parts: SnippetPart[] = [];
  let pos = 0;
  for (const [start, end] of marks) {
    if (end <= pos) continue;
    const from = Math.max(start, pos);
    if (from > pos) parts.push({ text: line.slice(pos, from), match: false });
    parts.push({ text: line.slice(from, end), match: true });
    pos = end;
  }
  if (pos < line.length) parts.push({ text: line.slice(pos), match: false });
  return parts;
}

/** The first line with a match, cut to a window around it. */
function snippet(note: LocalNoteText, words: readonly string[]): SnippetPart[] {
  const lines = [note.title, ...(note.content ?? "").split(/\r?\n/)];
  for (const raw of lines) {
    const line = raw.replace(/\s+/g, " ").trim();
    const lower = line.toLowerCase();
    const first = Math.min(
      ...words.map((w) => lower.indexOf(w)).filter((i) => i !== -1),
      Number.POSITIVE_INFINITY,
    );
    if (first === Number.POSITIVE_INFINITY) continue;
    const start = Math.max(0, first - 40);
    const cut = line.slice(start, start + SNIPPET_LENGTH);
    return highlight(start > 0 ? `…${cut}` : cut, words);
  }
  return [];
}

/**
 * FR5: every word must appear in the title or text (canvases: title only), case-insensitive.
 * Title matches first, then the most recently edited.
 */
export function searchLocalNotes(
  notes: readonly LocalNoteText[],
  query: string,
  options: { notebookId?: string; limit?: number } = {},
): LocalSearchResult[] {
  const words = searchWords(query);
  if (words.length === 0) return [];
  const hits: Array<{ note: LocalNoteText; inTitle: boolean }> = [];
  for (const note of notes) {
    if (options.notebookId && note.notebookId !== options.notebookId) continue;
    const title = note.title.toLowerCase();
    const text = `${title}\n${note.kind === "canvas" ? "" : (note.content ?? "").toLowerCase()}`;
    if (!words.every((w) => text.includes(w))) continue;
    hits.push({ note, inTitle: words.every((w) => title.includes(w)) });
  }
  hits.sort(
    (a, b) =>
      Number(b.inTitle) - Number(a.inTitle) || b.note.updatedAt.localeCompare(a.note.updatedAt),
  );
  return hits.slice(0, options.limit ?? 50).map(({ note }) => ({
    id: note.id,
    title: note.title,
    notebookId: note.notebookId,
    snippet: snippet(note, words),
  }));
}

/** Titles for `[[` completion: containing the text, starting with it first, newest first. */
export function matchTitles(
  notes: readonly LocalNoteText[],
  text: string,
  limit = 20,
): Array<{ id: string; title: string }> {
  const needle = text.trim().toLowerCase();
  return notes
    .filter((n) => n.title.toLowerCase().includes(needle))
    .sort(
      (a, b) =>
        Number(b.title.toLowerCase().startsWith(needle)) -
          Number(a.title.toLowerCase().startsWith(needle)) ||
        b.updatedAt.localeCompare(a.updatedAt),
    )
    .slice(0, limit)
    .map((n) => ({ id: n.id, title: n.title }));
}

/** Normalised title → id of the newest note with it (how the server resolves links). */
function titleIndex(notes: readonly LocalNoteText[]): Map<string, string> {
  const byKey = new Map<string, LocalNoteText>();
  for (const note of notes) {
    const key = linkKey(note.title);
    const known = byKey.get(key);
    if (!known || known.updatedAt < note.updatedAt) byKey.set(key, note);
  }
  return new Map([...byKey].map(([key, note]) => [key, note.id]));
}

/** The note's `[[links]]` and the local notes they point to (null = missing). */
export function resolveLocalLinks(
  content: string,
  notes: readonly LocalNoteText[],
): Array<{ target: string; noteId: string | null }> {
  const index = titleIndex(notes);
  return extractWikiLinks(content).map((l) => ({
    target: l.target,
    noteId: index.get(linkKey(l.target)) ?? null,
  }));
}

/** Notes linking to `title`, with the line of the link, sorted by title. */
export function localBacklinks(
  notes: readonly LocalNoteText[],
  title: string,
  exceptId: string,
): Array<{ noteId: string; title: string; line: string }> {
  const key = linkKey(title);
  if (key === "") return [];
  const found: Array<{ noteId: string; title: string; line: string }> = [];
  for (const note of notes) {
    if (note.id === exceptId || note.kind === "canvas" || !note.content) continue;
    const link = extractWikiLinks(note.content).find((l) => linkKey(l.target) === key);
    if (link) found.push({ noteId: note.id, title: note.title, line: link.line });
  }
  return found.sort((a, b) => a.title.localeCompare(b.title));
}

/** What the trash needs of a notebook (spec04 FR16, spec11 FR6a). */
export interface TrashNotebook {
  id: string;
  parentId: string | null;
  name: string;
  deletedAt: string | null;
}

/** What the trash needs of a note. */
export interface TrashNote {
  id: string;
  title: string;
  notebookId: string | null;
  deletedAt: string | null;
}

export type TrashEntry =
  | { kind: "note"; id: string; title: string; deletedAt: string }
  | { kind: "notebook"; id: string; title: string; noteCount: number; deletedAt: string };

/** Ids of all notebooks below `id`. */
export function notebookDescendants(notebooks: readonly TrashNotebook[], id: string): string[] {
  const below = notebooks.filter((n) => n.parentId === id);
  return below.flatMap((child) => [child.id, ...notebookDescendants(notebooks, child.id)]);
}

/**
 * The trash as the server lists it: a deleted notebook (the top of what went to the trash
 * together, with its note count) and single deleted notes; newest first.
 */
export function trashEntries(
  notebooks: readonly TrashNotebook[],
  notes: readonly TrashNote[],
): TrashEntry[] {
  const byId = new Map(notebooks.map((n) => [n.id, n]));
  const items: TrashEntry[] = [];
  for (const notebook of notebooks) {
    if (!notebook.deletedAt) continue;
    const parent = notebook.parentId ? byId.get(notebook.parentId) : undefined;
    if (parent?.deletedAt === notebook.deletedAt) continue;
    const together = notebooks.filter((n) => n.deletedAt === notebook.deletedAt);
    const ids = new Set([notebook.id, ...notebookDescendants(together, notebook.id)]);
    items.push({
      kind: "notebook",
      id: notebook.id,
      title: notebook.name,
      noteCount: notes.filter(
        (n) => n.notebookId && ids.has(n.notebookId) && n.deletedAt === notebook.deletedAt,
      ).length,
      deletedAt: notebook.deletedAt,
    });
  }
  for (const note of notes) {
    if (!note.deletedAt) continue;
    const notebook = note.notebookId ? byId.get(note.notebookId) : undefined;
    if (notebook?.deletedAt === note.deletedAt) continue;
    items.push({ kind: "note", id: note.id, title: note.title, deletedAt: note.deletedAt });
  }
  return items.sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
}
